// Per-place degraded-connectivity tracking for WIN Field v2 (card #4310:
// "Treat a store with repeated degraded connectivity as offline").
//
// field_network_health.js already detects a session-wide Degraded level with
// its own hysteresis (ADR-0110): quick to enter, slow to exit (>=30s dwell +
// 2 good samples). That's enough to shorten write timeouts and show the amber
// banner, but it resolves itself the moment the connection has one good run --
// which is exactly what a store that keeps tripping degraded and briefly
// recovering does (card #4310's source: a Tasker at IGA Summer Hill, place
// 676, logged "Connectivity entered degraded" 15 times in one morning while
// the session-wide level kept flipping back to Online in between). A Tasker
// stuck at a genuinely bad store still pays the full live NetworkFirst wait
// on place and Today page loads on every one of those brief "recovered"
// windows.
//
// This module adds a second, slower-moving signal scoped to a PLACE rather
// than the whole session: once a place racks up enough Degraded entries in a
// short window, it latches into "offline-first page loads" until a real,
// sustained recovery is observed (field:health-changed back to Online) or the
// Tasker leaves the place (the place-bound Stimulus controller disconnects).
//
// Page loads only. This never touches writes -- those stay governed by
// field_network_health.js's level + field_timeout_policy.js per ADR-0110,
// and photo upload / survey submit paths are explicitly out of scope for
// card #4310.
//
// Sensible defaults (stated in the card #4310 review notes): 3 Degraded
// entries within a rolling 10-minute window latch a place into offline-first.
//
// New leaf module, alongside field_network_health.js -- no imports from
// queue/sync/SW modules, so those modules (and the service worker, via its
// own OFFLINE_FIRST_PLACES message handler) can read this module's output
// without a cycle.

const ENTRY_THRESHOLD = 3
const WINDOW_MS        = 10 * 60 * 1000 // 10 minutes
const STORAGE_KEY       = "field:place-connectivity"

function _hasSessionStorage() {
  return typeof sessionStorage !== "undefined"
}

function _readState() {
  if (!_hasSessionStorage()) return {}
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}")
  } catch {
    return {}
  }
}

function _writeState(state) {
  if (!_hasSessionStorage()) return
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch { /* private mode / storage full -- in-session tracking only, never fatal */ }
}

function _key(placeId) {
  return String(placeId)
}

function _record(state, placeId) {
  const key = _key(placeId)
  return state[key] || { entries: [], latched: false, seeded: false }
}

// Record one Degraded entry for a place (called when the session's global
// level transitions INTO Degraded while this place is the active one, i.e.
// its Stimulus controller is connected). Returns true the moment this call
// is what crosses the place over the threshold into offline-first.
export function recordDegradedEntry(placeId) {
  if (placeId == null) return false
  const state  = _readState()
  const key    = _key(placeId)
  const now    = Date.now()
  const record = _record(state, placeId)

  record.entries = record.entries.filter((at) => now - at < WINDOW_MS)
  record.entries.push(now)

  const justLatched = !record.latched && record.entries.length >= ENTRY_THRESHOLD
  if (justLatched) record.latched = true

  state[key] = record
  _writeState(state)
  return justLatched
}

// Seed a place straight into offline-first from its own bad_connectivity flag
// (card #4310, slice 3) -- a place already known to have poor signal (manual
// toggle, or the server's own telemetry auto-set, ADR-0110 Card 4) does not
// need to re-earn three fresh entries this visit before its page loads stop
// waiting on the network. No-op if the place isn't flagged.
export function seedFromBadConnectivity(placeId, badConnectivity) {
  if (placeId == null || !badConnectivity) return
  const state  = _readState()
  const key    = _key(placeId)
  const record = _record(state, placeId)
  if (record.seeded) return

  record.seeded = true
  state[key] = record
  _writeState(state)
}

// Whether this place is currently latched into offline-first page loads,
// either from the bad_connectivity seed or from repeated Degraded entries
// this visit.
export function isOfflineFirst(placeId) {
  if (placeId == null) return false
  const state  = _readState()
  const record = state[_key(placeId)]
  return !!(record && (record.latched || record.seeded))
}

// Every place currently latched into offline-first -- read by the service
// worker bridge (posted via OFFLINE_FIRST_PLACES) and by display-only badges
// that don't carry their own placeId (e.g. the Today page).
export function offlineFirstPlaceIds() {
  const state = _readState()
  return Object.keys(state)
    .filter((key) => state[key]?.latched || state[key]?.seeded)
    .map(Number)
}

// A real, sustained recovery (field_network_health.js's own hysteresis
// already requires >=30s dwell + 2 good samples before declaring the session
// back Online) clears every place's threshold-earned latch -- "until signal
// recovers" (card #4310). Seeded flags are left alone: a place known bad from
// place.bad_connectivity stays that way until the flag itself changes, same
// as the manual-toggle/telemetry-auto-set source of truth it mirrors.
export function clearAllOnRecovery() {
  if (!_hasSessionStorage()) return
  const state = _readState()
  let changed = false
  for (const key of Object.keys(state)) {
    if (state[key]?.latched) {
      state[key].latched = false
      state[key].entries = []
      changed = true
    }
  }
  if (changed) _writeState(state)
}

// Card #4310 follow-up (note 3) -- disconnect() above only runs on a Turbo
// visit away from the place; a FULL page navigation (reload, deep link
// elsewhere, closing and reopening the tab) never runs it, and sessionStorage
// survives that regardless, so a threshold-earned latch from an earlier place
// this session could outlive the Tasker's visit to it. Called from connect()
// for whichever place is loading now: drops every OTHER place's
// threshold-earned latch, same reset clearAllOnRecovery already does (clear
// `latched` + `entries`, so a fresh 3 is needed to re-latch). A
// bad_connectivity SEED is left alone -- it tracks the place's own flag, not
// this visit, and connect() re-applies it fresh from the place's current
// value regardless.
export function dropOtherThresholdLatches(currentPlaceId) {
  if (!_hasSessionStorage()) return
  const state      = _readState()
  const currentKey = _key(currentPlaceId)
  let changed = false
  for (const key of Object.keys(state)) {
    if (key === currentKey) continue
    if (state[key]?.latched) {
      state[key].latched = false
      state[key].entries = []
      changed = true
    }
  }
  if (changed) _writeState(state)
}

// The Tasker leaves a place (its Stimulus controller disconnects -- see
// field_place_connectivity_controller.js) -- its latch and entry history
// reset entirely, so a later, unrelated visit to the same place starts fresh
// rather than inheriting this morning's trouble. The bad_connectivity seed is
// re-applied fresh on the next visit from the place's own current flag, not
// carried over from this cleared record.
export function leavePlace(placeId) {
  if (placeId == null) return
  const state = _readState()
  const key   = _key(placeId)
  if (!(key in state)) return
  delete state[key]
  _writeState(state)
}
